import type { PaginatedExtendedGroupByQuery } from '@/api/generated/model';
import { MULTIPLE_COLORS } from '@/constants/ui.constants';
import { DISTRIBUTION_STRIP_COVERAGE, SPEND_BY_MODEL_OTHER_LABEL } from '@/domains/pages/ai-spend/constants';
import { formatSpendGroupName } from '@/domains/pages/ai-spend/utils/map-ai-spend-distribution';
import { formatNumber } from '@/utils/format.util';

export type TaskModelSeries = {
  key: string;
  label: string;
  provider: string | null;
  color: string;
  data: Array<number | null>;
  tasksPerPoint: Array<number | null>;
  isOther?: boolean;
};

export type TaskModelChartData = { taskLabels: string[]; series: TaskModelSeries[] };

type ModelTaskCell = { cost: number; tasks: number };

type ModelAggregate = {
  totalCost: number;
  byTask: Map<string, ModelTaskCell>;
  providerCosts: Map<string | null, number>;
};

function isBlank(value: string | undefined | null): boolean {
  return !value?.trim();
}

function resolveDominantProvider(providerCosts: Map<string | null, number>): string | null {
  let bestProvider: string | null = null;
  let bestCost = -1;

  for (const [provider, cost] of providerCosts) {
    const normalizedProvider = isBlank(provider) ? null : provider!.trim();
    if (cost > bestCost) {
      bestCost = cost;
      bestProvider = normalizedProvider;
      continue;
    }

    if (cost !== bestCost) continue;

    const currentKey = normalizedProvider ?? '';
    const bestKey = bestProvider ?? '';
    if (currentKey.localeCompare(bestKey) < 0) {
      bestProvider = normalizedProvider;
    }
  }

  return bestProvider;
}

function getModelCostInTasks(aggregate: ModelAggregate, tasks: string[]): number {
  return tasks.reduce((sum, task) => sum + (aggregate.byTask.get(task)?.cost ?? 0), 0);
}

function buildOtherSeriesData(
  tailModels: string[],
  modelAggregates: Map<string, ModelAggregate>,
  selectedTasks: string[],
  pick: (cell: ModelTaskCell) => number,
): Array<number | null> {
  return selectedTasks.map((task) => {
    let sum = 0;
    let hasAny = false;

    for (const model of tailModels) {
      const cell = modelAggregates.get(model)?.byTask.get(task);
      if (!cell) continue;
      hasAny = true;
      sum += pick(cell);
    }

    return hasAny ? sum : null;
  });
}

export function mapTaskModelChartData(data: PaginatedExtendedGroupByQuery): TaskModelChartData {
  const modelAggregates = new Map<string, ModelAggregate>();
  const taskTotals = new Map<string, number>();

  for (const row of data.data ?? []) {
    const task = row.group_name?.trim();
    const model = row.group_name_2?.trim();
    if (!task || !model) continue;

    const provider = isBlank(row.group_name_3) ? null : row.group_name_3!.trim();
    const cost = row.cost ?? 0;
    const tasks = row.n_segments ?? 0;

    if (!modelAggregates.has(model)) {
      modelAggregates.set(model, { totalCost: 0, byTask: new Map(), providerCosts: new Map() });
    }

    const aggregate = modelAggregates.get(model)!;
    aggregate.totalCost += cost;

    const taskCell = aggregate.byTask.get(task) ?? { cost: 0, tasks: 0 };
    aggregate.byTask.set(task, {
      cost: taskCell.cost + cost,
      tasks: taskCell.tasks + tasks,
    });

    aggregate.providerCosts.set(provider, (aggregate.providerCosts.get(provider) ?? 0) + cost);
    taskTotals.set(task, (taskTotals.get(task) ?? 0) + cost);
  }

  if (taskTotals.size === 0) {
    return { taskLabels: [], series: [] };
  }

  // Every task stays in the chart, ordered by spend; the card scrolls horizontally to fit them.
  const selectedTasks = [...taskTotals.entries()].sort((left, right) => right[1] - left[1]).map(([task]) => task);

  const taskLabels = selectedTasks.map((task) => formatSpendGroupName(task, 'task'));

  const modelsInChart = new Set<string>();
  for (const task of selectedTasks) {
    for (const model of modelAggregates.keys()) {
      if (modelAggregates.get(model)?.byTask.has(task)) {
        modelsInChart.add(model);
      }
    }
  }

  const totalChartSpend = selectedTasks.reduce((sum, task) => sum + (taskTotals.get(task) ?? 0), 0);

  const orderedModels = [...modelsInChart]
    .map((model) => ({
      model,
      chartCost: getModelCostInTasks(modelAggregates.get(model)!, selectedTasks),
    }))
    .filter(({ chartCost }) => chartCost > 0)
    .sort((left, right) => right.chartCost - left.chartCost);

  const visibleModels: string[] = [];
  const tailModels: string[] = [];
  let cumulativeShare = 0;
  let thresholdReached = false;

  for (const { model, chartCost } of orderedModels) {
    const share = totalChartSpend > 0 ? chartCost / totalChartSpend : 0;

    if (thresholdReached) {
      tailModels.push(model);
      continue;
    }

    visibleModels.push(model);
    cumulativeShare += share;

    if (cumulativeShare >= DISTRIBUTION_STRIP_COVERAGE) {
      thresholdReached = true;
    }
  }

  const series: TaskModelSeries[] = visibleModels.map((model, index) => {
    const aggregate = modelAggregates.get(model)!;

    return {
      key: model,
      label: model,
      provider: resolveDominantProvider(aggregate.providerCosts),
      color: MULTIPLE_COLORS[index % MULTIPLE_COLORS.length]!,
      data: selectedTasks.map((task) => aggregate.byTask.get(task)?.cost ?? null),
      tasksPerPoint: selectedTasks.map((task) => aggregate.byTask.get(task)?.tasks ?? null),
    };
  });

  if (tailModels.length > 0) {
    series.push({
      key: '__other__',
      label: `${SPEND_BY_MODEL_OTHER_LABEL} (${formatNumber(tailModels.length)})`,
      provider: null,
      color: '#71717A',
      isOther: true,
      data: buildOtherSeriesData(tailModels, modelAggregates, selectedTasks, (cell) => cell.cost),
      tasksPerPoint: buildOtherSeriesData(tailModels, modelAggregates, selectedTasks, (cell) => cell.tasks),
    });
  }

  return { taskLabels, series };
}
